import { useState } from 'react';

import { Button } from '@/components/ui/button';

import { useAddPersonDialog } from './AddPeopleDialogs';
import { ClientCard } from './ClientCard';
import { linkButtonClass, SectionHeading } from './fields';
import { PersonKind } from './onboarding-model';
import { EntityCard, FamilyCard, ProfessionalCard } from './OtherPersonCards';
import { PersonCardProps } from './PersonCardShell';
import { OnboardingHousehold } from './useOnboardingHousehold';

const SECTIONS = [
    { kind: PersonKind.CLIENT, title: 'Clients', Card: ClientCard },
    { kind: PersonKind.ENTITY, title: 'Entities', Card: EntityCard },
    { kind: PersonKind.FAMILY, title: 'Family', Card: FamilyCard },
    { kind: PersonKind.PROFESSIONAL, title: 'Trusted Professionals', Card: ProfessionalCard },
];

export const ClientsView = ({
    householdGUID,
    household,
}: {
    householdGUID: string;
    household: OnboardingHousehold;
}) => {
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const dialog = useAddPersonDialog(householdGUID, household);
    const setAll = (value: boolean) =>
        setOpen(Object.fromEntries(household.people.map((person) => [person.profile.personOrgGUID, value])));
    const openCard = (personOrgGUID: string) => setOpen((current) => ({ ...current, [personOrgGUID]: true }));

    /** Opens Add person; the new person's card opens too. */
    const addPerson = (kind: PersonKind, onCreated: (personOrgGUID: string) => void = () => {}) =>
        dialog.addPerson(kind, (personOrgGUID) => {
            openCard(personOrgGUID);
            onCreated(personOrgGUID);
        });

    return (
        <div className="flex flex-col gap-9">
            <div className="-mb-5 flex justify-end gap-4">
                <button type="button" className={linkButtonClass} onClick={() => setAll(true)}>
                    Expand all
                </button>
                <button type="button" className={linkButtonClass} onClick={() => setAll(false)}>
                    Collapse all
                </button>
            </div>
            {SECTIONS.map(({ kind, title, Card }) => {
                const people = household.people.filter((person) => person.kind === kind);
                return (
                    <section key={kind} className="flex flex-col gap-3">
                        <SectionHeading
                            action={
                                <Button variant="outline" size="sm" onClick={() => addPerson(kind)}>
                                    Add
                                </Button>
                            }
                        >
                            {title}
                        </SectionHeading>
                        {people.length === 0 && (
                            <div className="border-border text-cocoa-400 rounded-lg border border-dashed px-5 py-4">
                                None added
                            </div>
                        )}
                        {people.map(({ profile }) => {
                            const props: PersonCardProps = {
                                householdGUID,
                                household,
                                profile,
                                open: !!open[profile.personOrgGUID],
                                onToggle: () =>
                                    setOpen((current) => ({
                                        ...current,
                                        [profile.personOrgGUID]: !current[profile.personOrgGUID],
                                    })),
                                addPerson,
                            };
                            // Kept across saves: a card takes up stored changes itself, so focus stays in its fields
                            return <Card key={profile.personOrgGUID} {...props} />;
                        })}
                    </section>
                );
            })}
            {dialog.dialog}
        </div>
    );
};
